import React, { useMemo } from "react";
import { Stack, Typography } from "@mui/material";
import ConnectionAccordionSection from "../../../../components/view/sections/ConnectionAccordionSection";
import ViewMultiSelectionRow from "../../../../components/view/fields/ViewMultiSelectionRow";
import EntityReferenceComponent from "../../../../components/entitiesReferences/EntityReferenceComponent";
import { ViewAccordionField } from "../../../../components/view/fields/ViewAccordion";
import usePersonQuery from "../../../../hooks/query/usePersonQuery";
import { Person } from "../../../../types/person";
import { PersonAssociatedVersion } from "../../../../types/version";
import { Team } from "../../../../types/team";
import {
  SYSTEM_TEAM_CONTRIBUTION_TYPE_LABELS,
  SystemTeamContributionTypes,
} from "../../../../types/versionTeam";
import {
  SYSTEM_CONTRIBUTION_TYPES_LABELS,
  SystemContributorTypes,
} from "../../../../types/contributor";

interface PersonAssociatedVersionsViewSectionProps {
  person: Person;
}

const formatRole = (role: string): string =>
  SYSTEM_TEAM_CONTRIBUTION_TYPE_LABELS[role as SystemTeamContributionTypes] ||
  SYSTEM_CONTRIBUTION_TYPES_LABELS[role as SystemContributorTypes] ||
  role;

const renderTeams = (teams: Team[] | null | undefined): React.ReactNode => {
  if (!teams || teams.length === 0) {
    return <Typography variant="body1">N/A</Typography>;
  }
  return (
    <Stack gap={1}>
      {teams.map((team) => (
        <EntityReferenceComponent key={team._key} entity={team} linkMode="view" />
      ))}
    </Stack>
  );
};

const getVersionFields = (
  version: PersonAssociatedVersion
): ViewAccordionField[] => {
  const roleLabels = Array.isArray(version?.roles)
    ? version.roles.map(formatRole)
    : [];
  const languages = Array.isArray(version?.language) ? version.language : [];

  return [
    {
      label: "Roles",
      value: <ViewMultiSelectionRow values={roleLabels} />,
    },
    {
      label: "Teams",
      value: renderTeams(version?.teams ?? null),
    },
    {
      label: "Language",
      value: <ViewMultiSelectionRow values={languages} />,
    },
    {
      label: "Work",
      value: version?.work ? (
        <EntityReferenceComponent entity={version.work} linkMode="view" />
      ) : (
        <Typography variant="body1">N/A</Typography>
      ),
    },
  ];
};

const PersonAssociatedVersionsViewSection: React.FC<
  PersonAssociatedVersionsViewSectionProps
> = ({ person }) => {
  const { useFetchAssociatedVersions } = usePersonQuery();
  const { data, isLoading, errorMessage } = useFetchAssociatedVersions(
    person?._key,
    {}
  );

  const items = useMemo<PersonAssociatedVersion[]>(
    () => data?.data ?? [],
    [data]
  );

  return (
    <ConnectionAccordionSection<PersonAssociatedVersion>
      title="Associated Versions"
      loading={isLoading}
      error={errorMessage}
      items={items}
      getItemId={(version, index) => version?._key ?? `version-${index}`}
      getItemTitle={(version) => (
        <EntityReferenceComponent entity={version} linkMode="view" />
      )}
      getItemFields={(version) => getVersionFields(version)}
      emptyText="No associated versions"
    />
  );
};

export default PersonAssociatedVersionsViewSection;
